import { create } from "zustand";

import { api } from "@/lib/api";
import type { ReadWordExportConfigDto } from "@package/types";
import type { AsyncState } from "./types";

interface WordExportState extends AsyncState {
  config: ReadWordExportConfigDto | null;
}

interface WordExportActions {
  loadConfig: () => Promise<void>;
  updateConfig: (isEnabled: boolean) => Promise<void>;
  uploadTemplate: (file: File) => Promise<void>;
  deleteTemplate: () => Promise<void>;
  reset: () => void;
}

type WordExportStore = WordExportState & WordExportActions;

const initialState: WordExportState = {
  config: null,
  isLoading: false,
  isLoaded: false,
  error: null
};

export const useWordExportStore = create<WordExportStore>()((set) => ({
  ...initialState,

  loadConfig: async () => {
    set({ isLoading: true, error: null });
    try {
      const config = await api.get<ReadWordExportConfigDto>(
        "/word-export/status"
      );
      set({ config, isLoading: false, isLoaded: true });
    } catch (error) {
      set({ isLoading: false, error: (error as Error).message });
    }
  },

  updateConfig: async (isEnabled: boolean) => {
    try {
      const config = await api.patch<ReadWordExportConfigDto>(
        "/word-export/config",
        { isEnabled }
      );
      set({ config, error: null });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  uploadTemplate: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const config = await api.postFormData<ReadWordExportConfigDto>(
        "/word-export/template",
        formData
      );
      set({ config, error: null });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  deleteTemplate: async () => {
    try {
      const config = await api.delete<ReadWordExportConfigDto>(
        "/word-export/template"
      );
      set({ config, error: null });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  reset: () => set(initialState)
}));
